import type { XGritCompleteProduct } from '../xgrit-api/xgrit-product';
import { applySchemaAdditions } from './schema-merge.util';
import type { SchemaOrgNode } from './schema-org-node.model';

/** Brand organization the Course cross-references via `provider`. */
export interface CourseProviderRef {
  /** Canonical org `@id` — must match the brand's Organization node (getBrandOrganizationId). */
  id: string;
  /** Brand / organization display name. */
  name: string;
}

/**
 * Builds a Schema.org Course node for an xGrit product, independent of which
 * UI component renders it. Deep-merges `craftData.schemaAdditions` (JSON string
 * from Craft) into the node so per-product structured-data overrides apply
 * everywhere the product appears (top-section, product-card, bundle, etc.).
 *
 * `provider` cross-references the brand Organization by its canonical `@id`
 * (passed in, not hardcoded) so the reference resolves to the single org node
 * the graph emits — and so it is correct per brand, not just Aceable. See EPS-3951.
 *
 * `pageUrl` is the canonical URL of the page rendering the product and becomes
 * `Course.url`; the xGrit `checkoutLink` stays on `Offer.url`, where the
 * transactional link belongs. Before EPS-4457 both were sourced from
 * `checkoutLink`, pointing `Course.url` at a checkout deep link on a host that
 * is `Disallow: /` in robots.txt.
 *
 * Scope of that defect, as verified against Google's Course documentation
 * (2026-08-04): `url` is absent from Google's Course property tables entirely —
 * not required, not recommended — so Google does not consume this property in
 * the current non-carousel shape and the fix changes no rich result. It is a
 * schema.org correctness fix (`Thing.url` is "URL of the item"), not an
 * SEO-visibility fix. Do not claim search impact for it.
 *
 * It does matter prospectively: Google's Course *carousel* variant requires
 * `ListItem.url` to be "the canonical URL of the item page." If an `ItemList`
 * wrapper is ever added here, a checkout link in this position becomes a real
 * validation error, so having it correct now is groundwork.
 *
 * The `hc=` coupon params on the old value were never the issue — they already
 * appear in the page's visible CTA hrefs and the checkout host is not
 * indexable. Two earlier revisions of this comment claimed otherwise; don't
 * reintroduce that rationale.
 *
 * Returns `null` when required fields (name, productId) are missing. The stable
 * `@id` (`urn:xgrit:course:{productId}`) lets `ServerSchemaService.register`
 * dedupe duplicate registrations of the same product — it is URN-based, not
 * URL-derived, so `pageUrl` never affects dedupe.
 */
export function getProductCourseSchema(
  product: XGritCompleteProduct | null | undefined,
  organization: CourseProviderRef,
  pageUrl: string,
): SchemaOrgNode | null {
  if (!product) return null;

  const name = product.apiData?.title || product.craftData?.title;
  const productId = product.craftData?.xgritProductId;

  if (!name || !productId) return null;

  const checkoutLink = product.apiData?.checkoutLink;
  const discountPrice = product.apiData?.discountPrice;
  const hasPrice = discountPrice != null && discountPrice > 0;
  const schoolInformation = product.apiData?.schoolInformation;

  const schema: SchemaOrgNode = {
    '@type': 'Course',
    '@id': `urn:xgrit:course:${productId}`,
    name,
    // Omitted rather than falling back to `checkoutLink` when the caller cannot
    // build a page URL (e.g. missing brandConfig.url): an absent `url` is a gap,
    // but a robots-blocked checkout link here is affirmatively wrong — it is the
    // EPS-4457 defect. Callers detect the misconfiguration by the omission.
    ...(pageUrl && { url: pageUrl }),
    ...(schoolInformation && { courseCode: schoolInformation }),
    provider: {
      '@id': organization.id,
      name: organization.name,
    },
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'online',
      inLanguage: 'en',
    },
  };

  if (hasPrice) {
    schema['offers'] = {
      '@type': 'Offer',
      price: discountPrice,
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      // The checkout deep link belongs here and ONLY here — `Offer.url` is the
      // transactional link, and a robots-blocked host is fine for that role.
      // Do not mirror it onto `Course.url` (see EPS-4457). Coupon/hardcode
      // params (`hc=`) are expected on this URL.
      ...(checkoutLink && { url: checkoutLink }),
    };
  }

  const description = product.apiData?.description || product.craftData?.disclaimer;
  if (description) {
    schema['description'] = description;
  }

  const seatTimeHours = product.apiData?.regulatorySeatTime;
  if (seatTimeHours && seatTimeHours > 0) {
    schema['timeRequired'] = `PT${seatTimeHours}H`;
  }

  return applySchemaAdditions(schema, product.craftData?.schemaAdditions);
}
